import { Accordion } from 'components/uikit/Accordion';
import { VariableAccessLevel } from '../variable-modal-data';
import { useScopedTranslation } from 'application/localization/useScopedTranslation';
import { VariablesByAccessLevel } from '../../VariablesList';
import {
  ExpandedAccordions,
  ExpandedAccordionsKey,
} from '../../variablesContext/VariablesContext';
import { VariableListItemDto } from 'services/api/api-client';
import { ReactNode } from 'react';
import styles from './SystemVariableAccordion.module.scss';
import { clsx } from 'clsx';
import { BrightTooltip } from 'components/tooltips/BrightTooltip';
import useElementTextOverflow, {
  OverflowDirection,
} from 'helpers/useElementTextOverflow';

type SystemVariableAccordionProps = {
  expanded: ExpandedAccordions;
  onToggle: (id: ExpandedAccordionsKey, isExpanded: boolean) => void;
  selectedAccessLevel?: VariableAccessLevel;
  variables: VariablesByAccessLevel;
  isLoading: boolean;
  renderVariableCard: (
    variable: VariableListItemDto,
    className?: string,
  ) => ReactNode;
  titleClassName?: string;
  initialDocumentId?: number | null;
  currentRevisionId?: number;
};

export const SystemVariableAccordion: React.FC<SystemVariableAccordionProps> = (
  props,
) => {
  const {
    expanded,
    onToggle,
    selectedAccessLevel,
    variables,
    isLoading,
    renderVariableCard,
    titleClassName,
    initialDocumentId,
    currentRevisionId,
  } = props;
  const i18n = useScopedTranslation('Page.Variables');

  const selectedLevelVariables =
    selectedAccessLevel === VariableAccessLevel.Local
      ? variables.local
      : variables.general;

  const {
    restVariablesByDocument,
    currentRevisionVariable,
    restRevisionsVariables,
    currentDocumentVariables,
  } = selectedLevelVariables.reduce(
    (acc, v) => {
      const documentId = v.basedOnDocument?.id;
      const revisionId = v.revisionId;

      if (revisionId) {
        if (revisionId === currentRevisionId) {
          acc.currentRevisionVariable = v;
        } else {
          acc.restRevisionsVariables.push(v);
        }
      }

      if (documentId) {
        if (initialDocumentId === Number(documentId))
          acc.currentDocumentVariables.push(v);
        else {
          if (!acc.restVariablesByDocument[documentId])
            acc.restVariablesByDocument[documentId] = [];

          acc.restVariablesByDocument[documentId].push(v);
        }
      }

      return acc;
    },
    {
      currentDocumentVariables: [] as VariableListItemDto[],
      restVariablesByDocument: {} as Record<number, VariableListItemDto[]>,
      currentRevisionVariable: undefined as VariableListItemDto | undefined,
      restRevisionsVariables: [] as VariableListItemDto[],
    },
  );

  return (
    <Accordion
      testId="auto-variable-list"
      title={i18n.t('system_variables')}
      replacementText={i18n.t('no_variables')}
      expanded={expanded.system}
      loading={isLoading}
      onExpandClick={() => onToggle('system', expanded.system)}
      titleClassName={titleClassName}
    >
      {currentRevisionVariable && renderVariableCard(currentRevisionVariable)}

      {currentDocumentVariables.length !== 0 && (
        <div className={styles.variableAccordionList}>
          <NestedSystemVariableAccordion
            systemVariables={currentDocumentVariables}
            renderVariableCard={renderVariableCard}
            titleClassName={titleClassName}
            key={initialDocumentId}
            isCurrent={true}
            expanded={expanded}
            onToggle={onToggle}
          />
        </div>
      )}

      {Object.keys(restVariablesByDocument).length !== 0 && (
        <div className={styles.variableAccordionList}>
          {Object.entries(restVariablesByDocument).map(([documentId, vars]) => (
            <NestedSystemVariableAccordion
              systemVariables={vars}
              renderVariableCard={renderVariableCard}
              titleClassName={titleClassName}
              key={documentId}
              isCurrent={initialDocumentId === Number(documentId)}
              expanded={expanded}
              onToggle={onToggle}
            />
          ))}
        </div>
      )}

      {restRevisionsVariables.length > 0 &&
        restRevisionsVariables.map((v) => renderVariableCard(v))}
    </Accordion>
  );
};

type NestedSystemVariableAccordionProps = {
  systemVariables: VariableListItemDto[];
  renderVariableCard: (
    variable: VariableListItemDto,
    className?: string,
  ) => ReactNode;
  titleClassName?: string;
  isCurrent: boolean;
  expanded: ExpandedAccordions;
  onToggle: (id: ExpandedAccordionsKey, isExpanded: boolean) => void;
};

const NestedSystemVariableAccordion: React.FC<
  NestedSystemVariableAccordionProps
> = (props) => {
  const i18n = useScopedTranslation('Page.Variables');
  const [contentRef, isOverflowing] = useElementTextOverflow<HTMLDivElement>(
    OverflowDirection.Horizontal,
  );

  const document = props.systemVariables[0].basedOnDocument;
  const id = document?.id!;
  const isExpanded =
    props.expanded[id] ?? (props.isCurrent || !!props.expanded.documents);
  const title = document?.name;

  return (
    <Accordion
      testId="nested-variable-list"
      title={
        <span className={styles.accordionTitle}>
          <BrightTooltip
            title={title}
            placement="top"
            disableHoverListener={!isOverflowing}
            PopperProps={{
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, -10],
                  },
                },
              ],
            }}
            enterDelay={300}
          >
            <span className={styles.tititle} ref={contentRef}>
              {title}
            </span>
          </BrightTooltip>
          {props.isCurrent && (
            <span className={styles.currentTag}>{i18n.t('current_tag')}</span>
          )}
        </span>
      }
      expanded={isExpanded}
      onExpandClick={() => props.onToggle(id, isExpanded)}
      titleClassName={clsx(
        props.titleClassName,
        styles.nestedVariableAccordion,
      )}
    >
      {props.systemVariables.map((v) =>
        props.renderVariableCard(v, styles.nestedVariableAccordion),
      )}
    </Accordion>
  );
};
